import { Button } from "@/shared/ui/button.tsx"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog.tsx"
import { format, isPast, isSameDay, startOfDay } from "date-fns"
import { CalendarIcon, Forward } from "lucide-react"

import { cn } from "@/lib/utils.ts"
import { Calendar } from "@/shared/ui/calendar.tsx"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover.tsx"
import { useEffect, useState } from "react"
import { SocialPostType } from "@/features/previous-post-view/types"
import { useSchedulePost, useUpdatePostBody } from "../edit-post-with-gallery/api/single-post-api.ts"

type Props = {
  data: SocialPostType["content"][number],
  location: "scheduled" | "review" | "discarded",
  onSchedule: (id: number) => void,
  eventIndex: number
}

const DateCheckDialog = ({ data, onSchedule, eventIndex, location }: Props) => {

  const [date, setDate] = useState<Date>()

  const { updatePostBody, isSuccess, isPending } = useUpdatePostBody()

  const { schedulePost } = useSchedulePost(location, onSchedule, eventIndex)

  const saveInputAndScheduleHandler = () => {
    updatePostBody({
      post_date: format(date!, 'yyyy-MM-dd'),
      post_time: data.post_time,
      post_id: data.post_id,
      hashtags: data.tags,
      post_content: data.post_content
    })
  }

  useEffect(() => {

    if (isSuccess) {
      // call schedule api here
      schedulePost(data.post_id)
    }

  }, [isSuccess])

  return (

    <Dialog modal={false}>
      <DialogTrigger asChild>
        <button
          disabled={!data.image_url}
          title={data.image_url ? "Schedule on social media" : "Select image to process"}
          type="button"
          className="flex gap-1 hover:bg-neutral-20- disabled:bg-neutral-200 rounded-xl cursor-pointer py-1 px-1 items-center text-gray-500">
          <Forward size={13} />
          <span className="text-[0.6rem] lg:text-xs">Approve</span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Schedule Post</DialogTitle>
          <DialogDescription>
            There seems to be a date mismatch, change the date and proceed
          </DialogDescription>
        </DialogHeader>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-full justify-start text-left font-normal",
                !date && "text-muted-foreground"
              )}
            >
              <CalendarIcon />
              {date ? format(date, "PPP") : <span>Pick a date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={date => {
                const today = startOfDay(new Date());
                const currentDate = startOfDay(date);
                return isPast(currentDate) && !isSameDay(currentDate, today);
              }}

              initialFocus
            />
          </PopoverContent>
        </Popover>
        <DialogFooter>
          <Button type="button"
            disabled={date === undefined || isPending}
            onClick={saveInputAndScheduleHandler}
          >
            Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DateCheckDialog;
